import ControlledTable from "@/components/controlled-table";
import { useTableServer } from "@/hooks/useTableServer";
import VoucherRepository from "@/utilities/repositories/Voucher";
import { useCallback, useMemo } from "react";
import useApi from "@/hooks/useApi";
import { IResponseData } from "@/utilities/types/requests";
import { IVoucher } from "@/utilities/types/Voucher";
import { getColumns } from "./columns";
import FilterElement from "./filter-element";
import { useParams } from "react-router-dom";

export default function VoucherTable() {
  const { promotionId } = useParams<{ promotionId?: string }>();

  const { request: deleteVoucher } = useApi<IResponseData<IVoucher>>({
    request: VoucherRepository.deleteVoucher,
    enableToast: true,
  });

  const onHeaderCellClick = (value: string) => ({
    onClick: () => {
      handleSort(value);
    },
  });

  const {
    isLoading,
    tableData,
    currentPage,
    totalItems,
    handlePaginate,
    pageSize,
    handleChangePageSize,
    searchTerm,
    handleSearch,
    sortConfig,
    handleSort,
    updateFilter,
    handleReset,
    refresh,
    filters,
  } = useTableServer({
    server: {
      request: (params) =>
        VoucherRepository.getAllVouchers(params, {
          promotionId: promotionId || undefined,
          used: true,
        }),
    },
    searchBy: (searchTerm) => {
      const s = searchTerm || undefined;
      return {
        search: s,
      };
    },
  });

  const onDeleteItem = useCallback(
    async (id: string) => {
      await deleteVoucher(id);
      handleReset(); // Recall API
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [handleReset],
  );

  const columns = useMemo(
    () =>
      getColumns({
        data: tableData,
        sortConfig,
        onHeaderCellClick,
        onDeleteItem,
        handleReset,
        refresh,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onHeaderCellClick, sortConfig.key, sortConfig.direction, onDeleteItem],
  );

  return (
    <div className="mt-4">
      <FilterElement
        onSearch={handleSearch}
        searchTerm={searchTerm}
        updateFilter={updateFilter}
        filters={filters}
      />
      <ControlledTable
        variant="modern"
        data={tableData}
        isLoading={isLoading}
        showLoadingText={true}
        // @ts-expect-error - The ControlledTable component expects a different column type
        columns={columns}
        paginatorOptions={{
          pageSize,
          setPageSize: handleChangePageSize,
          total: totalItems,
          current: currentPage,
          onChange: (page: number) => handlePaginate(page),
        }}
        className="rounded-md border border-muted text-sm shadow-sm [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:h-60 [&_.rc-table-placeholder_.rc-table-expanded-row-fixed>div]:justify-center [&_.rc-table-row:last-child_td.rc-table-cell]:border-b-0 [&_thead.rc-table-thead]:border-t-0"
      />
    </div>
  );
}
